// Display-side sanitizer for the `firstName` URL param.
// Returns null when the value is unsafe to render — caller falls back to control copy.
// Reason for being strict: a personalization arm with awkward output (all-caps, emoji, 60-char gibberish)
// is worse than no personalization.
const MAX_LENGTH = 24;
// Allow letters, combining marks, apostrophes, hyphens, and the plain space.
// Excludes \s broadly so tab/newline/CR (e.g. %0A) can't sneak through and break layout.
const ALLOWED = /^[\p{L}\p{M}'\- ]+$/u;

export function sanitizeFirstName(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const collapsed = raw.trim().replace(/ +/g, ' ');
  if (collapsed.length === 0 || collapsed.length > MAX_LENGTH) return null;
  if (!ALLOWED.test(collapsed)) return null;
  if (collapsed === collapsed.toUpperCase() && collapsed.length > 2) {
    return collapsed.charAt(0) + collapsed.slice(1).toLowerCase();
  }
  return collapsed;
}

// Accepts "7:00", "07:00", "7:00 AM", "7:00am", "10:40 PM", "22:00". Rejects everything else.
// 12-hour values (with AM/PM suffix) require hours 1-12; 24-hour values allow 0-23.
// Normalizes the AM/PM suffix to uppercase with a single space; leaves 24-hour values untouched.
export function sanitizeTime(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const match = raw.trim().match(/^([01]?\d|2[0-3]):([0-5]\d)\s?([AaPp][Mm])?$/);
  if (!match) return null;
  const [, hours, minutes, suffix] = match;
  if (suffix) {
    const hourNum = parseInt(hours, 10);
    if (hourNum < 1 || hourNum > 12) return null;
    return `${hours}:${minutes} ${suffix.toUpperCase()}`;
  }
  return `${hours}:${minutes}`;
}
